import styled from 'styled-components';
import { black, border1 } from 'utils/style/colors';

export interface IPrintTaskHeaderProps {
  size: 'small' | 'medium' | 'large';
}

const getHeaderSize: {
  [key in IPrintTaskHeaderProps['size']]: string;
} = {
  small: '8rem',
  medium: '12rem',
  large: '20rem',
};

export const LogoImage = styled('img')<{ border: string }>`
  width: 3.5cm;
  height: 3cm;
  padding-left: 0.5cm;
  margin-left: 7px;
  z-index: 1000;
  border-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      gray,
      rgba(0, 0, 0, 0)
    )
    1 100%;
  ${({ border }) => `border-${border}: 1px solid gray`}
`;

export const RibbonBoxContainer = styled('div')`
  color: ${black};
  width: 99vw;
  border: ${border1}
  display: flex;
  padding: 0 1rem 0;
  position: relative;
  // background: #eee;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); */
  margin-bottom: 0rem;
  border-radius: 10px;
  flex-direction: column;
`;

export const SubTasksTable = styled('table')`
  margin-bottom: 0.5rem;
  border-collapse: collapse;
  & th {
    color: var(--exact-white);
    font-weight: 500;
    background-color: #747474;
  }
  & td,
  th {
    padding: 0.3rem;
    max-width: 200px;
    text-align: center;
    border-radius: 0;
  }

  & tr:nth-child(even) {
    background-color: #eee;
  }
  & thead,
  tbody {
    border: 2px solid #747474;
  }
  & thead {
    & tr {
      & th {
        height: 2.5rem;
        padding: 0;
        font-size: 14px;
      }
    }
  }
  tbody {
    & tr {
      & th {
        height: 3rem;
        padding: 0;
      }
      & td {
        & span {
          font-size: 13px;
          font-weight: 400 !important; /* to ignore SingleTaskPart style */
          line-height: 1.7;
        }
      }
      & td:first-child {
        width: 2.5rem;
        color: var(--exact-white);
        font-size: 15px;
        font-weight: 500;
        background-color: #747474;
      }

      & td:first-child + td {
        text-align: right;
      }
    }
  }
`;

export const RibbonBoxTitle = styled('span')`
  top: 0.7rem;
  color: var(--exact-white);
  width: calc(100% + 2.8rem);
  margin: 0;
  height: 2.3rem;
  display: inline-flex;
  padding: 5px 30px;
  position: relative;
  font-size: 14px;
  background: #747474;
  /* box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3); */
  align-self: center;
  font-weight: 500;
  line-height: 1.3;
  align-items: center;
  /* text-shadow: 0 1px 1px #111; */
  border-radius: 3px 3px 0 0;
  justify-content: start;
  ::before,
  ::after {
    content: '';
    width: 0;
    height: 0;
    bottom: -10px;
    border: 5px solid;
    display: block;
    z-index: -10;
    position: absolute;
    border-color: #747474 transparent transparent transparent;
  }
  ::before {
    left: 0;
  }
  ::after {
    right: 0;
  }
`;

export const RibbonBoxBody = styled('span')`
  padding: 1.5rem 0.5rem 1rem 0.5rem;
  white-space: pre-wrap;
  font-weight: 400;
  font-size: 13px;
  line-height: 2;
  table {
    border: 3px solid ${black};
    width: 100%;
    text-align: left;
    border-collapse: collapse;
  }
  table td,
  table th {
    border: 1px solid ${black};
    padding: 5px 4px;
  }
  table.minimalistBlack thead {
    background: #cfcfcf;
    background: linear-gradient(
      to bottom,
      #dbdbdb 0%,
      #d3d3d3 66%,
      #cfcfcf 100%
    );
    border-bottom: 3px solid #000000;
  }
  table thead th {
    font-size: 15px;
    font-weight: bold;
    color: #000000;
    text-align: left;
  }
  & > table tfoot td {
    font-size: 14px;
  }
`;

export const PrintPage = styled('div')`
  page-break-after: always;
  display: flex;
  flex-direction: column;
`;

export const PrintSingleTaskHeader = styled('div')<IPrintTaskHeaderProps>`
  top: 0;
  width: 100%;
  display: flex;
  z-index: 1;
  flex-wrap: wrap;
  background: var(--exact-white);
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;

  & > * {
    flex: 1;
  }
`;

export const PrintSingleTaskHeaderWrapper = styled('div')`
  top: 0;
  width: 100%;
  display: flex;
  z-index: 1;
  flex-wrap: nowrap;
  background: var(--exact-white);
`;

export const PrintHeader = styled('thead')`
  border-bottom: none;
  display: block;
  @media print {
    display: table-header-group;
  }
`;

export const PrintSingleTaskContainer = styled('div')`
  @media print {
    & div {
      page-break-inside: auto !important;
    }
    & table {
      page-break-inside: auto !important;
    }
    & * {
      color-adjust: exact !important; /*Firefox*/
      -webkit-print-color-adjust: exact !important; /*Chrome, Safari */
    }
  }
`;

export const BreakPart = styled('span')`
  width: 0;
  height: 0;
  display: inline-flex;
  overflow: hidden;
  flex-basis: 100%;
`;

export const TD = styled('td')`
  padding: 0 5px;
`;

export const TableHeaderSpace = styled('div')<IPrintTaskHeaderProps>`
  height: ${(props) => getHeaderSize[props.size]};
`;

export const SplitLine = styled('hr')`
  border: 0;
  height: 1px;
  flex-basis: 100%;
  background: -webkit-linear-gradient(
    to left,
    hsla(0, 0%, 25%, 0) 0%,
    hsla(0, 0%, 25%, 0.7) 50%,
    hsla(0, 0%, 25%, 0) 100%
  );
  background: -moz-linear-gradient(
    to left,
    hsla(0, 0%, 25%, 0) 0%,
    hsla(0, 0%, 25%, 0.7) 50%,
    hsla(0, 0%, 25%, 0) 100%
  );
`;

export const SingleTaskPart = styled('span')`
  font-size: 14px;
  & > span:first-child {
    font-weight: 500;
  }
  line-height: 2.3rem;
`;

export const WithoutSubTasks = styled('span')`
  display: flex;
  font-weight: 400;
  justify-content: center;
`;
